import { Fab, Theme, useMediaQuery } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { FC, memo, ReactElement, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Button, { ButtonProps, sanitizeButtonRestProps } from './Button';
import { useTranslate } from "../../customHooks";


/**
 * Opens the Create view of a given resource
 *
 * Renders as a regular button on desktop, and a Floating Action Button
 * on mobile.
 *
 */
const CreateButton: FC<CreateButtonProps> = props => {
    const {
        basePath = '',
        className,
        classes: classesOverride,
        label = 'ra.action.create',
        scrollToTop = true,
        variant,
        ...rest
    } = props;
    const classes = useStyles(props);
    const translate = useTranslate();
    const isSmall = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down('sm')
    );
    const resource = '';
    const location = useMemo(
        () => ({
            pathname: basePath ? `${basePath}/create` : `/${resource}/create`,
            state: { _scrollToTop: scrollToTop },
        }),
        [basePath, resource, scrollToTop]
    );
    return isSmall ? (
        <Fab
            component={Link}
            color="primary"
            className={classnames(classes.floating, className)}
            to={location}
            aria-label={label && translate(label)}
            {...sanitizeButtonRestProps(rest)}
        >
        </Fab>
    ) : (
        <Button
            style={{ textTransform: 'capitalize', }}
            component={Link}
            to={location}
            className={classnames(classes.button, className)}
            label={label}
            variant={variant}
            {...(rest as any)}
        >
        </Button>
    );
};

const useStyles = makeStyles(
    theme => ({
        floating: {
            color: theme.palette.getContrastText(theme.palette.primary.main),
            margin: 0,
            top: 'auto',
            right: 20,
            bottom: 60,
            left: 'auto',
            position: 'fixed',
            zIndex: 1000,
        },
        button: {
            textTransform: 'capitalize',
        },
    }),
    { name: 'RaCreateButton' }
);

interface Props {
    basePath?: string;
    icon?: ReactElement;
    scrollToTop?: boolean;
}

export type CreateButtonProps = Props & ButtonProps;

CreateButton.propTypes = {
    basePath: PropTypes.string,
    classes: PropTypes.object,
    className: PropTypes.string,
    icon: PropTypes.element,
    label: PropTypes.string,
};

export default memo(CreateButton, (prevProps, nextProps) => {
    return (
        prevProps.basePath === nextProps.basePath &&
        prevProps.label === nextProps.label &&
        prevProps.translate === nextProps.translate &&
        prevProps.to === nextProps.to &&
        prevProps.disabled === nextProps.disabled
    );
});
